import cn from 'classnames';
import { CSSProperties, FC } from 'react';

import { ToothStatus } from '@/shared/config';
import { useAppSelector } from '@/shared/hooks';

import { organizationModel } from '@/entities/organization';
import { isThirdMolarTooth } from '@/entities/tooth';

import { ToothIcon } from '../ToothIcon/ToothIcon';

import styles from './ToothView.module.scss';

export type ToothViewClickParams = {
  id: string;
  ISONumber: number;
  isActive: boolean;
};

export type ToothViewClick = (props: ToothViewClickParams) => void;

export type ToothViewProps = {
  className?: string;
  style?: CSSProperties;
  id: string;
  ISONumber: number;
  viewNumber?: number | string;
  status?: ToothStatus;
  disabled?: boolean;
  isLowerJaw: boolean;
  isSupernumerary?: boolean;
  isActive?: boolean;
  isROIOpen?: boolean;
  isSimplify?: boolean;
  blackAndWhiteMode?: boolean;
  toothOfInterestNumber?: number;
  isFocused?: boolean;
  onClick?: ToothViewClick;
  isApproved?: boolean;
};

export const ToothView: FC<ToothViewProps> = (props) => {
  const {
    className,
    style,
    id,
    viewNumber,
    ISONumber,
    status,
    disabled,
    isActive = false,
    isROIOpen,
    isSimplify,
    isLowerJaw,
    isSupernumerary,
    toothOfInterestNumber,
    blackAndWhiteMode = false,
    isFocused,
    onClick,
    isApproved,
  } = props;

  const { isFDA } = useAppSelector(
    organizationModel.selectors.selectFDAEnvironment,
  );

  const isThirdMolar = isThirdMolarTooth(ISONumber);
  const isToothMissing = status === ToothStatus.missing;
  const isMissingThirdMolar = isThirdMolar && isToothMissing;

  const handleToothClick = () => {
    if (onClick && !disabled) {
      onClick({ id, ISONumber, isActive });
    }
  };

  const wrapperStatus =
    isMissingThirdMolar || (isFDA && isToothMissing)
      ? ToothStatus.healthy
      : status;

  return (
    <div
      style={{ ...style }}
      className={cn(
        isSimplify ? styles.simplyContainer : styles.container,
        {
          [styles.currentActiveTooth]: toothOfInterestNumber === viewNumber,
          [styles.notActive]: !isActive,
          [styles.pointer]: isROIOpen || isActive,
          [styles.isLowerJaw]: isLowerJaw,
          [styles.disabled]: disabled,
          [styles.focused]: isFocused,
          [`approvedToothWrapper`]: isApproved && isFDA,
          [styles.simplifyNotActive]: !isActive && isSimplify,
        },
        blackAndWhiteMode
          ? styles.blackAndWhitePrintToothWrapper
          : `${wrapperStatus}ToothWrapper`,
        className,
      )}
      onClick={handleToothClick}
    >
      <span
        className={cn(
          styles.position,
          blackAndWhiteMode && styles.blackAndWhitePrintToothNumberColor,
          `${wrapperStatus}ToothIcon`,
          'p4',
        )}
      >
        {`${viewNumber}${isSupernumerary ? 'S' : ''}`}
      </span>

      {!isSimplify && (
        <div className={styles.iconContainer}>
          <ToothIcon
            isMissing={isToothMissing}
            isThirdMolar={isThirdMolar}
            position={ISONumber}
            blackAndWhiteMode={blackAndWhiteMode}
            toothStatus={status}
            className={cn(`${wrapperStatus}ToothIcon`)}
          />
        </div>
      )}
    </div>
  );
};
